import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Users, GraduationCap, Megaphone, CalendarDays,
  ClipboardCheck, Images, School, Wallet, QrCode, Tag, FileText, UserCog,
  Store, Package, Image as ImageIcon, Settings, LayoutGrid, MessagesSquare, Smartphone,
} from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar,
} from "@/components/ui/sidebar";
import schoolLogo from "@/assets/logo-mi-almunajah.png.asset.json";
import { canAccess, useUserRole, type AppRole } from "@/hooks/use-user-role";
import { useUnreadChats } from "@/hooks/use-unread-chats";

type Item = { title: string; url: string; icon: React.ComponentType<{ className?: string }> };

const mainItems: Item[] = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
];

const dataItems: Item[] = [
  { title: "Data Siswa", url: "/students", icon: Users },
  { title: "Data Guru", url: "/teachers", icon: GraduationCap },
  { title: "Profil Sekolah", url: "/school", icon: School },
  { title: "Anggota & Akses", url: "/members", icon: UserCog },
];

const academicItems: Item[] = [
  { title: "Chat Guru & Staff", url: "/chat", icon: MessagesSquare },
  { title: "Pengumuman", url: "/announcements", icon: Megaphone },
  { title: "Pengumuman Staf", url: "/staff-announcements", icon: Megaphone },
  { title: "Jadwal Pelajaran", url: "/schedules", icon: CalendarDays },
  { title: "Absensi", url: "/attendance", icon: ClipboardCheck },
  { title: "Galeri", url: "/gallery", icon: Images },
];

const financeItems: Item[] = [
  { title: "Jenis Pembayaran", url: "/payment-types", icon: Tag },
  { title: "Tagihan Siswa", url: "/student-bills", icon: FileText },
  { title: "Verifikasi Pembayaran", url: "/payments", icon: Wallet },
  
  { title: "Pengaturan Pembayaran Manual", url: "/payment-settings", icon: QrCode },
];

const storeItems: Item[] = [
  { title: "Dashboard Store", url: "/store", icon: Store },
  { title: "Kategori", url: "/store/categories", icon: LayoutGrid },
  { title: "Produk", url: "/store/products", icon: Package },
  { title: "Banner", url: "/store/banners", icon: ImageIcon },
  { title: "Pengaturan", url: "/store/settings", icon: Settings },
];

const systemItems: Item[] = [
  { title: "OTA Update Aplikasi", url: "/app-updates", icon: Smartphone },
];

function filterByRole(items: Item[], role: AppRole | null) {
  return items.filter((i) => canAccess(role, i.url));
}

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const isActive = (p: string) => pathname === p || pathname.startsWith(p + "/");
  const { role } = useUserRole();
  const { total: chatUnread } = useUnreadChats();

  const main = filterByRole(mainItems, role);
  const data = filterByRole(dataItems, role);
  const academic = filterByRole(academicItems, role);
  const finance = filterByRole(financeItems, role);
  const store = filterByRole(storeItems, role);
  const system = role === "superadmin" ? systemItems : [];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b">
        <div className="flex items-center gap-3 px-2 py-3">
          <div className="w-9 h-9 rounded-xl bg-white border flex items-center justify-center shrink-0 shadow-[var(--shadow-elevation-1)] overflow-hidden">
            <img src={schoolLogo.url} alt="Logo MI Plus Almunajah" className="w-full h-full object-contain" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-bold text-sm leading-tight truncate">MI Plus Almunajah</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Admin Panel</div>
            </div>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {main.length > 0 && <NavGroup label="Utama" items={main} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
        {data.length > 0 && <NavGroup label="Data Master" items={data} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
        {academic.length > 0 && <NavGroup label="Akademik" items={academic} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
        {finance.length > 0 && <NavGroup label="Keuangan" items={finance} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
        {store.length > 0 && <NavGroup label="Toko Sekolah Al-Munajah Store" items={store} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
        {system.length > 0 && <NavGroup label="Sistem" items={system} isActive={isActive} collapsed={collapsed} chatUnread={chatUnread} />}
      </SidebarContent>
    </Sidebar>
  );
}

function NavGroup({
  label, items, isActive, collapsed, chatUnread,
}: {
  label: string;
  items: Item[];
  isActive: (p: string) => boolean;
  collapsed: boolean;
  chatUnread: number;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const handleClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarGroup>
      {!collapsed && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                <Link to={item.url} className="flex items-center gap-3" onClick={handleClick}>
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 truncate">{item.title}</span>
                  {item.url === "/chat" && chatUnread > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                      {chatUnread > 99 ? "99+" : chatUnread}
                    </span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
